import { useEffect, useRef, useState } from 'react';
import { createPO, type DealRef, type RawTile, type Supplier } from './logistics-api';

// Создание заявки поставщику (ТЗ §5). Обязательна только позиция; остальное — по желанию.
export function NewPOModal({
  suppliers,
  deals,
  raw,
  presetSupplierId,
  preset,
  onClose,
  onCreated,
}: {
  suppliers: Supplier[];
  deals: DealRef[];
  raw: RawTile[];
  presetSupplierId?: number;
  // Предзаполнение из «Плана закупа»: позиция, привязка к складу и кол-во дефицита.
  preset?: { what?: string; raw_id?: number | null; qty_num?: number };
  onClose: () => void;
  onCreated: () => void;
}) {
  const [supplierId, setSupplierId] = useState<string>(
    presetSupplierId ? String(presetSupplierId) : '',
  );
  const [what, setWhat] = useState(preset?.what ?? '');
  const [dealId, setDealId] = useState('');
  const [rawId, setRawId] = useState(preset?.raw_id ? String(preset.raw_id) : '');
  const [qty, setQty] = useState(preset?.qty_num ? String(preset.qty_num) : '');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const linkedRaw = rawId ? raw.find((r) => r.id === Number(rawId)) : undefined;

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const position = what.trim();
    if (!position) {
      setMsg('Укажите позицию.');
      inputRef.current?.focus();
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const linked = rawId ? Number(rawId) : null;
      const qtyN = linked && Number(qty) > 0 ? Number(qty) : undefined;
      await createPO({
        supplier_id: supplierId ? Number(supplierId) : undefined,
        what: position,
        deal_id: dealId ? Number(dealId) : null,
        // Со связью со складом кол-во — число (для авто-прихода); иначе свободный текст.
        qty: linked ? undefined : qty.trim() || undefined,
        qty_num: qtyN,
        raw_id: linked,
        amount: amount ? Number(amount) : undefined,
      });
      onCreated();
    } catch {
      setMsg('Не удалось создать заявку. Попробуйте ещё раз.');
      setBusy(false);
    }
  }

  return (
    <div className="ovl" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-h">
          <div className="grow">
            <div className="m-cli">Новая заявка поставщику</div>
            <div className="m-no">статус «Заявка» · дальше по потоку до склада</div>
          </div>
          <button className="icon-btn close-x" onClick={onClose}>
            ✕
          </button>
        </div>
        <form className="mbody" onSubmit={submit}>
          {msg && <p className="err">{msg}</p>}
          <label className="fld">
            <span className="fld-l">Поставщик</span>
            <select
              value={supplierId}
              disabled={busy}
              onChange={(e) => setSupplierId(e.target.value)}
            >
              <option value="">— без поставщика —</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className="fld">
            <span className="fld-l">
              Позиция <span className="req">*</span>
            </span>
            <input
              ref={inputRef}
              value={what}
              disabled={busy}
              placeholder="напр. Лист 3мм ст3 / Мотор-редуктор"
              list="raw-positions"
              onChange={(e) => setWhat(e.target.value)}
            />
            <datalist id="raw-positions">
              {raw.map((r) => (
                <option key={r.id} value={r.name} />
              ))}
            </datalist>
          </label>
          <label className="fld">
            <span className="fld-l">Под сделку</span>
            <select value={dealId} disabled={busy} onChange={(e) => setDealId(e.target.value)}>
              <option value="">— без привязки —</option>
              {deals.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.no}
                </option>
              ))}
            </select>
          </label>
          <label className="fld">
            <span className="fld-l">Пополняет позицию склада</span>
            <select
              value={rawId}
              disabled={busy}
              onChange={(e) => {
                setRawId(e.target.value);
                const r = raw.find((x) => x.id === Number(e.target.value));
                if (r && !what.trim()) setWhat(r.name); // авто-подстановка названия позиции
              }}
            >
              <option value="">— не привязывать к складу —</option>
              {raw.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} ({r.qty} {r.unit})
                </option>
              ))}
            </select>
            {linkedRaw && (
              <span className="muted small">
                При статусе «На складе» остаток пополнится на введённое кол-во.
              </span>
            )}
          </label>
          <div style={{ display: 'flex', gap: 10 }}>
            <label className="fld" style={{ flex: 1 }}>
              <span className="fld-l">Кол-во{linkedRaw ? `, ${linkedRaw.unit}` : ''}</span>
              <input
                type={linkedRaw ? 'number' : 'text'}
                min="0"
                step="any"
                value={qty}
                disabled={busy}
                placeholder={linkedRaw ? 'напр. 120' : 'напр. 120 т'}
                onChange={(e) => setQty(e.target.value)}
              />
            </label>
            <label className="fld" style={{ flex: 1 }}>
              <span className="fld-l">Сумма, ₸</span>
              <input
                type="number"
                min="0"
                value={amount}
                disabled={busy}
                placeholder="необязательно"
                onChange={(e) => setAmount(e.target.value)}
              />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 6 }}>
            <button type="button" className="btn ghost sm" disabled={busy} onClick={onClose}>
              Отмена
            </button>
            <button type="submit" className="btn sm" disabled={busy}>
              {busy ? 'Создаём…' : 'Создать заявку'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
